import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, FilterIcon, PlusIcon, ArrowUpDown } from 'lucide-react';
import { ClientCard } from '../components/ClientCard';
import { BottomNav } from '../components/BottomNav';
import { useClients } from '../hooks/useClients';
import { ClientStatusFilterChips } from '../components/ClientStatusFilterChips';
import { MediumFilterChips } from '../components/MediumFilterChips';
import type { ClientMedium } from '../types/clients';

type SortOption = 'name-asc' | 'name-desc' | 'interaction-asc' | 'interaction-desc';

export function Clients() {
  const navigate = useNavigate();
  const { data: clients, isLoading, isError, refetch } = useClients();
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [sortOption, setSortOption] = useState<SortOption>('interaction-desc');
  const [filters, setFilters] = useState({
    hasReminder: false,
    paymentDue: false,
    inactive30: false
  });

  // Filter chip state - by default no filters are active (all clients are visible)
  const [selectedStatuses, setSelectedStatuses] = useState<Set<'active' | 'past'>>(
    new Set()
  );
  const [selectedMediums, setSelectedMediums] = useState<Set<ClientMedium>>(
    new Set()
  );
  const formattedClients = useMemo(() => {
    if (!clients) return [];

    return clients.map(client => ({
      id: client.id,
      name: client.name,
      phone: client.phone,
      status: (client.status ?? 'lead') as 'lead' | 'active' | 'past' | 'cold',
      goal: client.goal ?? 'No goal added yet',
      whatsappJid: client.whatsapp_jid ?? null,
      medium: (client.medium ?? 'online') as ClientMedium,
    }));
  }, [clients]);

  const filteredAndSortedClients = useMemo(() => {
    // First, filter the clients
    const filtered = formattedClients.filter(client => {
      const query = searchQuery.toLowerCase();
      const matchesSearch = client.name.toLowerCase().includes(query) || client.phone.includes(searchQuery) || client.goal.toLowerCase().includes(query);

      // Filter by status chips (active/past only, leads are always shown)
      // If no status filters selected, show all (active and past)
      const matchesStatusFilter = client.status === 'lead' ||
        selectedStatuses.size === 0 ||
        selectedStatuses.has(client.status as 'active' | 'past');

      // Filter by medium chips
      // If no medium filters selected, show all
      const matchesMediumFilter = selectedMediums.size === 0 || selectedMediums.has(client.medium);

      return matchesSearch && matchesStatusFilter && matchesMediumFilter;
    });

    // Then, sort the filtered clients
    const sorted = [...filtered].sort((a, b) => {
      switch (sortOption) {
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'interaction-asc': {
          const aTime = clients?.find(c => c.id === a.id)?.last_interaction_at;
          const bTime = clients?.find(c => c.id === b.id)?.last_interaction_at;
          const aMs = aTime ? new Date(aTime).getTime() : 0;
          const bMs = bTime ? new Date(bTime).getTime() : 0;
          return aMs - bMs;
        }
        case 'interaction-desc': {
          const aTime = clients?.find(c => c.id === a.id)?.last_interaction_at;
          const bTime = clients?.find(c => c.id === b.id)?.last_interaction_at;
          const aMs = aTime ? new Date(aTime).getTime() : 0;
          const bMs = bTime ? new Date(bTime).getTime() : 0;
          return bMs - aMs;
        }
        default:
          return 0;
      }
    });

    return sorted;
  }, [formattedClients, searchQuery, selectedStatuses, selectedMediums, sortOption, clients]);

  // Toggle handlers for filter chips
  const handleToggleStatus = (status: 'active' | 'past') => {
    setSelectedStatuses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(status)) {
        newSet.delete(status);
      } else {
        newSet.add(status);
      }
      return newSet;
    });
  };

  const handleToggleMedium = (medium: ClientMedium) => {
    setSelectedMediums(prev => {
      const newSet = new Set(prev);
      if (newSet.has(medium)) {
        newSet.delete(medium);
      } else {
        newSet.add(medium);
      }
      return newSet;
    });
  };

  // Calculate counts for filter chips
  const statusCounts = {
    active: formattedClients.filter(c => c.status === 'active').length,
    past: formattedClients.filter(c => c.status === 'past').length,
  };

  const mediumCounts = {
    online: formattedClients.filter(c => c.medium === 'online').length,
    offline: formattedClients.filter(c => c.medium === 'offline').length,
  };

  const renderContent = () => {
    if (isLoading) {
      return <div className="p-4 space-y-4">
          {[...Array(3)].map((_item, index) => <div key={index} className="bg-white rounded-2xl p-5 shadow-sm animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/3 mb-4" />
              <div className="h-3 bg-gray-200 rounded w-2/3 mb-2" />
              <div className="h-3 bg-gray-200 rounded w-1/2" />
            </div>)}
        </div>;
    }

    if (isError) {
      return <div className="p-4">
          <div className="bg-white rounded-2xl shadow-sm p-6 text-center">
            <h3 className="text-lg font-semibold text-[#212121] mb-2">
              Unable to load clients
            </h3>
            <p className="text-gray-600 mb-4">
              Please check your connection and try again.
            </p>
            <button onClick={() => refetch()} className="bg-[#5B8E87] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#4F7A74] transition-colors">
              Retry
            </button>
          </div>
        </div>;
    }

    if (filteredAndSortedClients.length === 0) {
      return <div className="p-4">
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl">👥</span>
            </div>
            <h3 className="text-lg font-semibold text-[#212121] mb-2">
              No clients found
            </h3>
            <p className="text-gray-600 mb-6">
              {formattedClients.length
                ? 'Try a different search or filter.'
                : 'Add your first client to get started with your CRM.'}
            </p>
            <button onClick={() => navigate('/add-client')} className="bg-[#5B8E87] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#4F7A74] transition-colors">
              ➕ Add Client
            </button>
          </div>
        </div>;
    }

    return <div className="p-4">
        <div className="space-y-4">
          {filteredAndSortedClients.map(client => <ClientCard key={client.id} client={client} />)}
        </div>
      </div>;
  };

  return <div className="min-h-screen bg-[#F5F5F5] pb-20">
      <div className="bg-white shadow-sm sticky top-0 z-20">
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-semibold text-[#212121]">Clients</h1>
            <div className="flex items-center gap-2">
              <div className="relative">
                <button
                  onClick={() => setShowSortMenu(!showSortMenu)}
                  className="p-2 rounded-lg transition-colors text-gray-600 hover:bg-gray-100"
                  aria-label="Sort options"
                >
                  <ArrowUpDown className="w-5 h-5" />
                </button>

                {showSortMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowSortMenu(false)}
                    />
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                      <div className="py-1">
                        <button
                          onClick={() => {
                            setSortOption('name-asc');
                            setShowSortMenu(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                            sortOption === 'name-asc' ? 'bg-gray-100 font-medium' : ''
                          }`}
                        >
                          Name (A → Z)
                        </button>
                        <button
                          onClick={() => {
                            setSortOption('name-desc');
                            setShowSortMenu(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                            sortOption === 'name-desc' ? 'bg-gray-100 font-medium' : ''
                          }`}
                        >
                          Name (Z → A)
                        </button>
                        <button
                          onClick={() => {
                            setSortOption('interaction-desc');
                            setShowSortMenu(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                            sortOption === 'interaction-desc' ? 'bg-gray-100 font-medium' : ''
                          }`}
                        >
                          Last Interaction (Recent First)
                        </button>
                        <button
                          onClick={() => {
                            setSortOption('interaction-asc');
                            setShowSortMenu(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                            sortOption === 'interaction-asc' ? 'bg-gray-100 font-medium' : ''
                          }`}
                        >
                          Last Interaction (Oldest First)
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <button onClick={() => setShowFilters(!showFilters)} className={`p-2 rounded-lg transition-colors ${showFilters ? 'bg-[#5B8E87] text-white' : 'text-gray-600'}`}>
                <FilterIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="relative mb-3">
            <SearchIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search by name, number, or goal..." className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all" />
          </div>

          {/* Filter chips - single row layout like Chats view */}
          <div className="flex items-center gap-2 flex-wrap">
            <ClientStatusFilterChips
              selectedStatuses={selectedStatuses}
              onToggleStatus={handleToggleStatus}
              counts={statusCounts}
            />
            <div className="w-px h-4 bg-gray-300" />
            <MediumFilterChips
              selectedMediums={selectedMediums}
              onToggleMedium={handleToggleMedium}
              counts={mediumCounts}
            />
          </div>
        </div>
      </div>
      {showFilters && <div className="bg-white border-b border-gray-200 p-4 animate-slide-down">
          <h3 className="text-sm font-semibold text-[#212121] mb-3">
            Filters & Sort
          </h3>
          <div className="space-y-2 mb-4">
            <label className="flex items-center gap-3">
              <input type="checkbox" checked={filters.hasReminder} onChange={e => setFilters({
            ...filters,
            hasReminder: e.target.checked
          })} className="w-5 h-5 rounded border-gray-300 text-[#5B8E87] focus:ring-[#5B8E87]" />
              <span className="text-sm text-[#212121]">Has Reminder</span>
            </label>
            <label className="flex items-center gap-3">
              <input type="checkbox" checked={filters.paymentDue} onChange={e => setFilters({
            ...filters,
            paymentDue: e.target.checked
          })} className="w-5 h-5 rounded border-gray-300 text-[#5B8E87] focus:ring-[#5B8E87]" />
              <span className="text-sm text-[#212121]">Payment Due</span>
            </label>
            <label className="flex items-center gap-3">
              <input type="checkbox" checked={filters.inactive30} onChange={e => setFilters({
            ...filters,
            inactive30: e.target.checked
          })} className="w-5 h-5 rounded border-gray-300 text-[#5B8E87] focus:ring-[#5B8E87]" />
              <span className="text-sm text-[#212121]">Inactive &gt;30d</span>
            </label>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#212121] mb-2">
              Sort By
            </label>
            <select className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all">
              <option>Last Interaction</option>
              <option>Next Reminder</option>
              <option>Progress %</option>
              <option>Name (A-Z)</option>
            </select>
          </div>
        </div>}
      {renderContent()}
      <button onClick={() => navigate('/add-client')} className="fixed bottom-24 right-6 bg-[#5B8E87] text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:bg-[#4F7A74] transition-all active:scale-95 z-30">
        <PlusIcon className="w-6 h-6" />
      </button>
      <BottomNav />
    </div>;
}
